import { ArrowDownward, ArrowUpward } from '@mui/icons-material'
import { Box, Button, Stack } from '@dtx-company/design-system/src'
import { LoggedOutGeneratorSection } from '../LoggedOutCodeGenerator.types'
import { selectSection } from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'
import React from 'react'
import useTranslation from 'next-translate/useTranslation'

export const FormActionButtons: React.FC = () => {
  const { locgService } = useGlobalServices()
  const currentSection = useSelector(locgService, selectSection)
  const scanDestinationSectionActive = useSelector(locgService, state =>
    state.matches('scanDestination')
  )
  const codeDesignSectionActive = useSelector(locgService, state => state.matches('codeDesign'))
  const isValidating = useSelector(locgService, state =>
    state.matches('scanDestination.validating')
  )

  const { t } = useTranslation('logged-out-generator')
  const { isMobileOrTablet } = useScreenSizeBreakpoints()
  const leftButtonProps = scanDestinationSectionActive
    ? {
        color: 'primary' as const,
        variant: 'contained' as const,
        startIcon: ArrowDownward,
        onClick: () =>
          locgService.send({ type: 'NEXT', target: LoggedOutGeneratorSection.CodeDesign }),
        loading: isValidating,
        children: t('LoggedOutGenerator.FormActionButtons.ContinueToDesign')
      }
    : {
        color: 'secondary' as const,
        variant: 'outlined' as const,
        startIcon: ArrowUpward,
        onClick: () =>
          locgService.send({ type: 'BACK', target: LoggedOutGeneratorSection.ScanDestination }),
        children: isMobileOrTablet
          ? t('LoggedOutGenerator.FormActionButtons.ScanDestination')
          : t('LoggedOutGenerator.FormActionButtons.BackToScanDestination')
      }

  return (
    <Stack
      gap={{ xs: 2, md: 4 }}
      sx={theme => ({
        position: {
          xs: 'fixed',
          md: 'absolute'
        },
        bottom: 0,
        left: 0,
        width: '100%',
        background: `linear-gradient(rgba(255, 255, 255, 0), ${theme.palette.surface[0]} 20%)`,
        px: { xs: 4.75, md: 8 },
        py: { xs: 6, md: 15 },
        zIndex: 5,
        justifyContent: { xs: 'center', md: 'left' }
      })}
      direction="row"
    >
      <Box
        sx={theme => ({
          display: 'inline-flex',
          flex: {
            xs: 1,
            md: 'initial'
          },
          minWidth: 'fit-content',
          button: {
            px: {
              xs: 3.5,
              md: 4
            },
            py: {
              xs: 2.5,
              md: 3
            },
            fontSize: {
              xs: theme.spacing(3.5),
              md: theme.spacing(4)
            },
            width: {
              xs: '100%',
              md: 'auto'
            }
          }
        })}
      >
        <Button
          size="large"
          {...leftButtonProps}
          data-heap-id="LoggedOutGenerator_FormActionButtons_NavigationButton"
          data-section={currentSection}
          data-action={scanDestinationSectionActive ? 'ContinueToDesign' : 'BackToScanDestination'}
        />
      </Box>

      <Box
        sx={theme => ({
          display: 'inline-flex',
          flexShrink: 0,
          flex: {
            xs: 1,
            md: 'initial'
          },
          button: {
            px: {
              xs: 3.25,
              md: 3.75
            },
            py: {
              xs: 2.25,
              md: 3
            },
            fontSize: {
              xs: theme.spacing(3.5),
              md: theme.spacing(4)
            },
            width: {
              xs: '100%',
              md: 'auto'
            }
          }
        })}
      >
        <Button
          size="large"
          color={codeDesignSectionActive ? 'primary' : 'secondary'}
          variant={codeDesignSectionActive ? 'contained' : 'outlined'}
          onClick={() =>
            locgService.send({
              type: 'DOWNLOAD_CODE',
              target: LoggedOutGeneratorSection.SignUp
            })
          }
          data-heap-id="LoggedOutGenerator_FormActionButtons_DownloadCodeButton"
          data-section={currentSection}
        >
          {t('LoggedOutGenerator.FormActionButtons.DownloadCode')}
        </Button>
      </Box>
    </Stack>
  )
}
